'use client'

import { useState } from 'react'
import Link from 'next/link'
import { Menu, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useRouter } from 'next/navigation'
import { Logo } from '@/components/logo'

// Define the props interface for HeroHeader
interface HeroHeaderProps {
  openAuthDrawer?: (tab: 'signin' | 'signup') => void;
}

export function HeroHeader({ openAuthDrawer }: HeroHeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const router = useRouter()

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen)
  }

  const openSignIn = () => {
    // Use the passed in function from parent if available
    if (openAuthDrawer) {
      openAuthDrawer('signin');
    }
  }

  const openSignUp = () => {
    if (openAuthDrawer) {
      openAuthDrawer('signup');
    }
  }

  // Custom smooth scroll function
  const smoothScroll = (e: React.MouseEvent<HTMLAnchorElement>, targetId: string) => {
    e.preventDefault()
    const targetElement = document.getElementById(targetId.replace('#', ''))
    const headerElement = document.querySelector('header') // Get header element
    
    if (targetElement && headerElement) {
      const headerHeight = headerElement.offsetHeight
      const buffer = 20 // Add a small buffer for padding
      const targetPosition = targetElement.offsetTop - headerHeight - buffer

      // Close mobile menu if it's open
      if (isMenuOpen) {
        setIsMenuOpen(false)
      }
      
      // Scroll to the calculated position
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth',
      })
      
      // Update URL without causing a page reload
      // Note: Using window.scrollTo might cause slight differences in how browsers handle history updates compared to scrollIntoView.
      // If issues arise, consider delaying the history push or alternative methods.
      window.history.pushState({}, '', targetId)
    }
  }

  // Define navigation sections
  const navLinks = [
    { href: "#features", label: "Features" },
    { href: "#integrations", label: "Integrations" },
    { href: "#testimonials", label: "Testimonials" },
    { href: "#pricing", label: "Pricing" },
    { href: "#footer", label: "Contact" }
  ]

  return (
    <header className="fixed left-0 top-0 z-50 w-full bg-transparent">
      <div className="backdrop-blur-md bg-background/70 relative flex h-16 w-full items-center justify-between px-6 md:h-20 md:px-16">
        <Link
          href="/"
          className="flex items-center gap-2">
          <span className="sr-only">NewsClipping</span>
          <Logo className="size-7" />
          <span className="hidden text-xl font-bold md:block text-white">
             m
            <span
              style={{
                background: 'linear-gradient(to right, #A855F7, #EC4899)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              ai
            </span>
            d.
          </span>
        </Link>

        {/* Centered Navigation for Medium+ Screens */}
        <div className="hidden flex-1 items-center justify-center md:flex">
          <nav className="flex items-center gap-1 text-sm lg:gap-2">
            {/* New CTA Button */}
            <Button 
              asChild
              variant="ghost" // Or choose another variant like 'outline' or 'default'
              className="mr-4 bg-[#8A6FE8]/10 text-[#8A6FE8] hover:bg-[#8A6FE8]/20 hover:text-[#8A6FE8] font-semibold" // Example styling
            >
              <Link href="/dashboard">Monitor Your Brand Now</Link>
            </Button>
            
            {/* Existing Links */}
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => smoothScroll(e, link.href)}
                className="rounded-md px-3 py-2 font-semibold transition-colors hover:bg-white/10">
                {link.label}
              </a>
            ))}
          </nav>
        </div>

        {/* Right Aligned Auth Buttons for Medium+ Screens */}
        <div className="hidden items-center gap-4 md:flex">
          <Button 
            variant="outline"
            className="gap-2 text-white border-white/40 hover:bg-white/10 hover:text-white"
            onClick={openSignIn}>
            Sign In
          </Button>
          <Button 
            variant="default"
            className="gap-2 bg-[#8A6FE8] hover:bg-[#7A5CD4] text-white"
            onClick={openSignUp}>
            Sign Up
          </Button>
        </div>

        <div className="flex items-center gap-4 md:hidden">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleMenu}
            className="rounded-full hover:bg-accent">
            {isMenuOpen ? <X className="size-5" /> : <Menu className="size-5" />}
            <span className="sr-only">Toggle menu</span>
          </Button>
        </div>

        {isMenuOpen && (
          <div className="absolute inset-x-0 top-16 bg-background/100 shadow-lg md:hidden">
            <nav className="flex flex-col p-4 bg-[#1E1E2E]">
              {/* New CTA Button for Mobile */}
              <Button 
                asChild
                variant="ghost"
                className="w-full justify-center mb-2 bg-[#8A6FE8]/10 text-[#8A6FE8] hover:bg-[#8A6FE8]/20 hover:text-[#8A6FE8] font-semibold"
                onClick={() => setIsMenuOpen(false)} // Close menu on click
              >
                <Link href="/dashboard">Monitor Your Brand Now</Link>
              </Button>
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => smoothScroll(e, link.href)}
                  className="rounded-md px-3 py-2 font-semibold transition-colors hover:bg-white/10">
                  {link.label}
                </a>
              ))}
              <div className="mt-4 flex flex-col gap-2">
                <Button 
                  variant="outline"
                  className="w-full justify-center text-white border-white/40 hover:bg-white/10 hover:text-white"
                  onClick={() => {
                    setIsMenuOpen(false)
                    openSignIn()
                  }}>
                  Sign In
                </Button>
                <Button 
                  variant="default"
                  className="w-full justify-center bg-[#8A6FE8] hover:bg-[#7A5CD4] text-white"
                  onClick={() => {
                    setIsMenuOpen(false)
                    openSignUp()
                  }}>
                  Sign Up
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  )
} 